import { POWERUP_DROP_BASE, POWERUP_DROP_VY, ULTI_FILL_KILL } from '../constants';
import { Ev } from '../events';
import { grantXp } from './progressionSystem';
import { PickupKind } from '../pools/PickupPool';
import type { Rng } from '../rng';
import type { World } from '../World';

/** Tire le TYPE d'un power-up qui tombe, avec des poids fixes. Réparation la plus
 *  rare (une vie pèse lourd), le reste réparti. Un seul tirage `rng.int(100)` pour
 *  rester lisible et déterministe. */
function choosePowerup(rng: Rng): number {
  const r = rng.int(100);
  if (r < 6) return PickupKind.Repair; // 6 % — une vie
  if (r < 32) return PickupKind.Bomb; // 26 %
  if (r < 58) return PickupKind.Surge; // 26 %
  if (r < 80) return PickupKind.Coolant; // 22 %
  return PickupKind.Overdrive; // 20 %
}

/**
 * Traite les morts, une fois que toutes les collisions du tick sont résolues.
 *
 * Séparer « appliquer les dégâts » (collisionSystem) de « traiter la mort »
 * (ici) évite une classe entière de bugs : sans cette séparation, deux
 * projectiles touchant le même ennemi dans le même tick peuvent le tuer deux
 * fois, émettre deux explosions et créditer deux fois le score. Ici la mort est
 * constatée une seule fois, après coup.
 */
export function damageSystem(w: World): void {
  const en = w.enemies;

  // En arrière : la boucle libère.
  for (let i = en.count - 1; i >= 0; i--) {
    const s = en.at(i);
    if (en.hp[s]! > 0) continue;

    const x = en.x[s]!;
    const y = en.y[s]!;
    const value = en.score[s]!;

    w.kills++;
    w.score += value;
    grantXp(w, value);
    // La jauge d'ulti se remplit au combat : un kill la charge d'un cran.
    w.player.ulti = Math.min(1, w.player.ulti + ULTI_FILL_KILL);
    // On publie le RANG, pas l'identifiant de l'ennemi. La présentation dose
    // ainsi le gel et la secousse sans jamais connaître un archétype en
    // particulier : ajouter un ennemi ne demande de toucher ni au Director ni
    // au rendu.
    const def = w.defs.enemies[en.kind[s]!];
    w.events.push(Ev.EnemyKilled, def?.rank ?? 0, x, y, value);
    // Un BOSS abattu mérite sa fanfare, pas l'explosion générique de l'EnemyKilled.
    if (def?.boss) w.events.push(Ev.BossKilled, 0, x, y, 0);

    /* Le porteur meurt : toutes ses parties meurent avec lui, immédiatement.
       Les laisser vivre produirait des tourelles orphelines flottant à la
       position d'un vaisseau qui n'existe plus — et `motionSystem` finirait par
       les nettoyer un tick plus tard, donc avec un artefact visible. */
    const myUid = en.uid[s]!;
    for (let j = en.count - 1; j >= 0; j--) {
      const c = en.at(j);
      if (c !== s && en.parent[c] === s && en.parentUid[c] === myUid) en.free(c);
    }

    const drops = def ? def.drops : 1 + w.rng.int(3);
    for (let d = 0; d < drops; d++) {
      const slot = w.pickups.spawn(
        PickupKind.Cell,
        x,
        y,
        w.rng.spread(90),
        w.rng.range(40, 190),
      );
      if (slot < 0) {
        w.overflow.pickups++;
        break;
      }
    }

    /* POWER-UP : chute RARE d'un bonus instantané, d'autant plus probable que
       l'ennemi est gros (rang). Il descend franchement (`POWERUP_DROP_VY`) avec un
       léger étalement horizontal, pour se distinguer de la gerbe de monnaie. Le
       tirage consomme `w.rng` → change l'empreinte de replay (golden re-béni). */
    const rank = def?.rank ?? 0;
    if (w.rng.next() < POWERUP_DROP_BASE * (1 + rank)) {
      const kind = choosePowerup(w.rng);
      if (w.pickups.spawn(kind, x, y, w.rng.spread(50), -POWERUP_DROP_VY) < 0) {
        w.overflow.pickups++;
      }
    }

    en.free(s);
  }
}
